<template>
  <div class="pa-5" style="min-height: calc(100vh - 120px); height: 100%;">
    <div class="h-100">
      <v-card class="rounded-lg h-100">
        <v-card-text class="d-flex flex-column align-center h-100">

          <v-chip
            v-if="!selectedBooster && activeLuckBoost"
            color="purple"
            variant="tonal"
            prepend-icon="mdi-clover"
            class="mb-3"
            size="large"
          >
            🍀 Boost de luck actif : <strong class="ml-1">+{{ Math.round((activeLuckBoost.multiplier - 1) * 100) }}%</strong>
            <span class="ml-2 text-caption">(encore {{ activeLuckBoost.label }})</span>
          </v-chip>

          <BoosterSelection v-if="!selectedBooster" :boosters="boosters" @select="selectBooster" />

          <BoosterOpening v-else :booster="selectedBooster" :cards="cards" :status="status" :openCount="openCount" @skip="skipAnimation" @reset="resetBooster" @reopen="reopenBooster" />

        </v-card-text>
      </v-card>

    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/store/user.js'
import { useDataStore } from '@/store/data.js'
import Booster from '@/classes/Booster.js'
import Card from '@/classes/Card.js'
import Collection from '@/classes/Collection.js'
import achievementsManager from '@/assets/functions/achievementsManager.js'
import Swal from 'sweetalert2/dist/sweetalert2.js'
import logsManager from '@/assets/functions/logsManager.js'
import BoosterSelection from '@/components/boosters/BoosterSelection.vue'
import BoosterOpening from '@/components/boosters/BoosterOpening.vue'
import { LEGENDARY_TYPE, LEGENDARY_FORCED_VARIANT, MAX_LEGENDARY_PER_BOOSTER } from '@/classes/Settings.js'
import { fullBoosterCards, isFullBooster, targetedCollectionIds } from '@/assets/functions/fullBooster.js'
import notifManager from '../assets/functions/notifManager'
import Notif from '@/classes/Notif.js'

export default {
  components: {
    BoosterSelection,
    BoosterOpening
  },
  data() {
    return {
      notifsCleaned: false,
      unsub: [],
      userStore: useUserStore(),
      dataStore: useDataStore(),
      status: 'idle', // idle, opening, exploded, revealed
      selectedBooster: null,
      openCount: 1,
      cards: [],
      boosters: [], // This will be the expanded inventory list
      timeouts: [],
    }
  },
  computed: {
    allBoosters() {
      return this.dataStore.boosters
    },
    allCards() {
      return this.dataStore.cards
    },
    collections() {
      return this.dataStore.collections
    },
    extensions() {
      return this.dataStore.extensions
    },
    settings() {
      return this.dataStore.settings
    },
    activeLuckBoost() {
      const boost = this.userStore.profile?.luckBoost
      if (!boost || !boost.expiresAt || boost.expiresAt <= Date.now()) return null
      const remaining = boost.expiresAt - Date.now()
      const hours = Math.floor(remaining / 3600000)
      const minutes = Math.floor((remaining % 3600000) / 60000)
      return {
        multiplier: boost.multiplier,
        label: hours > 0 ? `${hours}h${String(minutes).padStart(2, '0')}` : `${minutes}min`,
      }
    },
  },
  created() {
    Promise.all([
      this.dataStore.bindBoosters(),
      this.dataStore.bindCards(),
      this.dataStore.bindCollections(),
      this.dataStore.bindExtensions(),
      this.dataStore.bindSettings()
    ]).then(() => {
      this.loadInventory();
    });

    if (this.userStore.profile) {
      this.deleteBoosterNotifs();
      this.notifsCleaned = true;
    }
  },
  watch: {
    'userStore.profile': {
      handler() {
        this.loadInventory();
        if (!this.notifsCleaned && this.userStore.profile) {
          this.deleteBoosterNotifs();
          this.notifsCleaned = true;
        }
      },
      deep: true
    },
    allBoosters() {
      this.loadInventory();
    },
    collections() {
      this.loadInventory();
    },
    extensions() {
      this.loadInventory();
    },
  },
  methods: {
    loadInventory() {
      this.boosters = [];
      if (!this.userStore.profile || !this.userStore.profile.boosters) return;

      // Iterate through user's booster inventory
      for (const [boosterId, amount] of Object.entries(this.userStore.profile.boosters)) {
        if (amount <= 0) continue;

        const boosterDef = this.allBoosters.find(b => b.id === boosterId);
        if (!boosterDef) continue;

        const targetCollections = Array.isArray(boosterDef.targetCollections)
          ? boosterDef.targetCollections
          : [];
        const targetExtensions = Array.isArray(boosterDef.targetExtensions)
          ? boosterDef.targetExtensions.filter((extId) => {
              const ext = this.extensions.find(e => e.id === extId);
              return !!ext && ext.isPublic !== false;
            })
          : [];
        const extensionCollections = targetExtensions
          .map(extId => this.extensions.find(e => e.id === extId)?.collection)
          .filter(Boolean);
        const accessCollections = [...new Set([...targetCollections, ...extensionCollections])];

        // Check if user has access to at least one targeted collection
        if (this.userStore.profile.collections && accessCollections.some(colId => this.userStore.profile.collections[colId] === true)) {
          // Add single entry with count
          this.boosters.push({
            ...boosterDef,
            count: amount
          });
        }
      }

      // Sort by collection number, then booster size (number of cards), then name
      this.boosters.sort((a, b) => {
        const firstTargetA = (a.targetCollections && a.targetCollections.length) ? a.targetCollections[0] : a.collection;
        const firstTargetB = (b.targetCollections && b.targetCollections.length) ? b.targetCollections[0] : b.collection;
        const colA = this.collections.find(c => c.id === firstTargetA);
        const colB = this.collections.find(c => c.id === firstTargetB);
        const numColA = colA ? (colA.number || 0) : 0;
        const numColB = colB ? (colB.number || 0) : 0;

        if (numColA !== numColB) return numColA - numColB;
        if (firstTargetA !== firstTargetB) return (firstTargetA || '').localeCompare(firstTargetB || '');
        if (a.size !== b.size) return (a.size || 0) - (b.size || 0);
        return (a.name || '').localeCompare(b.name || '');
      });
    },
    selectBooster(booster, quantity = 1) {
      this.selectedBooster = booster;
      this.openCount = quantity;
      this.openBooster();
    },

    async openBooster() {
      if (this.status !== 'idle') return;
      if (!this.userStore.profile) return;
      if (!this.settings) {
        Swal.fire('Erreur', 'Paramètres non chargés.', 'error');
        return;
      }

      this.status = 'opening';

      // --- Logic Phase ---

      // 1. Get cards for configured targets (base collections and/or extensions)
      const targetCollections = Array.isArray(this.selectedBooster.targetCollections)
        ? this.selectedBooster.targetCollections
        : [];
      const targetExtensions = Array.isArray(this.selectedBooster.targetExtensions)
        ? this.selectedBooster.targetExtensions
        : [];

      const selectedExtensionIds = new Set(
        targetExtensions.filter((extId) => {
          const ext = this.extensions.find(e => e.id === extId);
          return !!ext && ext.isPublic !== false;
        })
      );

      const extensionCollectionIds = new Set(
        [...selectedExtensionIds]
          .map(extId => this.extensions.find(e => e.id === extId)?.collection)
          .filter(Boolean)
      );

      let effectiveCollectionIds = new Set([...targetCollections, ...extensionCollectionIds]);

      /*
        LE BOOSTER FULL NE TIRE RIEN, ET NE LIT PAS SA LISTE D'EXTENSIONS.

        Il reprend la collection TELLE QU'ELLE EST AUJOURD'HUI — une carte
        ajoutée hier en fait partie, une extension publiée ce matin aussi —, là
        où `targetExtensions` n'est qu'une photographie prise au dernier
        enregistrement. La règle est écrite une seule fois, dans
        `assets/functions/fullBooster.js`, et c'est la même que celle qui
        remplit le champ « Taille » de l'administration : ce qui est annoncé en
        boutique est donc ce qui est distribué ici.
      */
      const isFull = isFullBooster(this.selectedBooster);

      const fullCards = isFull
        ? fullBoosterCards(this.selectedBooster, {
            cards: this.allCards,
            extensions: this.extensions,
            collections: this.collections,
          })
        : [];

      if (isFull) {
        effectiveCollectionIds = new Set(targetedCollectionIds(this.selectedBooster, this.extensions));
      }

      const collectionCards = this.allCards.filter((c) => {
        const inTargetCollection = effectiveCollectionIds.has(c.collection);
        if (!inTargetCollection) return false;

        // No extension on card => included only if base collection is targeted.
        if (!c.extension) return targetCollections.includes(c.collection);

        // Extension card => included only if this extension is explicitly targeted.
        return selectedExtensionIds.has(c.extension);
      });

      if ((isFull ? fullCards : collectionCards).length === 0) {
        Swal.fire('Erreur', 'Aucune carte trouvée pour ce booster.', 'error');
        this.status = 'idle';
        this.selectedBooster = null;
        return;
      }

      // Probabilities from settings (percentages)
      let typeRates = this.settings.typeDropRates || {
        'common': 100,
        'uncommon': 0,
        'rare': 0,
        'mythic': 0,
        'legendary': 0
      };

      let variantRates = this.settings.rarityDropRates || {
        'common': 100,
        'silver': 0,
        'golden': 0,
        'foil': 0
      };

      // Appliquer le boost de luck actif du joueur
      const luckBoost = this.userStore.profile?.luckBoost
      if (luckBoost && luckBoost.expiresAt > Date.now()) {
        const mult = luckBoost.multiplier
        // Variantes : multiplier en partant de la plus rare, plafonner à 100%
        let foil   = (variantRates.foil   || 0) * mult
        let golden = (variantRates.golden || 0) * mult
        let silver = (variantRates.silver || 0) * mult
        let total  = foil + golden + silver
        if (total > 100) {
          let excess = total - 100
          const silverRed = Math.min(silver, excess)
          silver -= silverRed; excess -= silverRed
          if (excess > 0) {
            const goldenRed = Math.min(golden, excess)
            golden -= goldenRed; excess -= goldenRed
          }
          if (excess > 0) foil -= Math.min(foil, excess)
        }
        variantRates = { foil, golden, silver }
        // Types (poids) : booster les raretés supérieures
        typeRates = {
          ...typeRates,
          uncommon:  (typeRates.uncommon  || 0) * mult,
          rare:      (typeRates.rare      || 0) * mult,
          mythic:    (typeRates.mythic    || 0) * mult,
          legendary: (typeRates.legendary || 0) * mult,
        }
      }

      // Determine how many boosters to actually open
      const availableCount = this.userStore.profile.boosters[this.selectedBooster.id] || 0;
      const actualCount = Math.min(this.openCount || 1, availableCount);

      if (actualCount === 0) {
        Swal.fire('Erreur', 'Vous n\'avez plus de booster de ce type.', 'error');
        this.status = 'idle';
        this.selectedBooster = null;
        return;
      }

      const allGeneratedCards = [];
      const boosterSize = this.selectedBooster.size || 0;

      // Draw cards for each booster independently
      for (let b = 0; b < actualCount; b++) {
        /*
          LE CAS FULL : aucun tirage, aucune rareté, aucune variante.
          « Un exemplaire de toutes les cartes en variante classique » — la
          phrase est la fonction. Ouvrir trois boosters Full donne donc trois
          exemplaires de chaque carte, comme trois boosters ordinaires donnent
          trois fois leur contenu.
        */
        if (isFull) {
          fullCards.forEach((card) => {
            allGeneratedCards.push({
              ...card,
              originalType: card.type,
              frontImage: card.image,
              type: 'common',
            });
          });
          continue;
        }

        // Fresh card pool for each booster draw
        const availableCardsByRarity = {};
        collectionCards.forEach(card => {
          if (!availableCardsByRarity[card.type]) availableCardsByRarity[card.type] = [];
          availableCardsByRarity[card.type].push(card);
        });

        // Une seule carte légendaire maximum par booster.
        let legendaryCountInBooster = 0;

        for (let i = 0; i < boosterSize; i++) {
          const legendaryAllowed = legendaryCountInBooster < MAX_LEGENDARY_PER_BOOSTER;

          // A. Determine Rarity Slot based on rates and availability
          const listAvailableRarities = () => Object.keys(availableCardsByRarity)
            .filter(r => availableCardsByRarity[r].length > 0)
            .filter(r => legendaryAllowed || r !== LEGENDARY_TYPE);

          let availableRarities = listAvailableRarities();

          // Si aucune rareté n'a de cartes, recharger le pool complet
          if (availableRarities.length === 0) {
            collectionCards.forEach(card => {
              if (!availableCardsByRarity[card.type]) availableCardsByRarity[card.type] = [];
              availableCardsByRarity[card.type].push(card);
            });
            availableRarities = listAvailableRarities();
          }

          if (availableRarities.length === 0) break;

          let totalRarityWeight = 0;
          availableRarities.forEach(r => {
            totalRarityWeight += (typeRates[r] || 0);
          });

          // If no weights match for available cards, default to equal chance among available
          let effectiveTypeRates = typeRates;
          if (totalRarityWeight === 0) {
            // La légendaire n'est jamais distribuée "par défaut" : elle n'entre dans
            // la répartition équiprobable que si aucune autre rareté n'est disponible.
            const fallbackRarities = availableRarities.some(r => r !== LEGENDARY_TYPE)
              ? availableRarities.filter(r => r !== LEGENDARY_TYPE)
              : availableRarities;
            totalRarityWeight = fallbackRarities.length;
            effectiveTypeRates = {};
            fallbackRarities.forEach(r => effectiveTypeRates[r] = 1);
            availableRarities = fallbackRarities;
          }

          let rngRarity = Math.random() * totalRarityWeight;
          let selectedRarity = availableRarities[availableRarities.length - 1];

          for (const r of availableRarities) {
            rngRarity -= (effectiveTypeRates[r] || 0);
            if (rngRarity <= 0) {
              selectedRarity = r;
              break;
            }
          }

          // B. Pick a card from that rarity pool
          const pool = availableCardsByRarity[selectedRarity];
          const cardIndex = Math.floor(Math.random() * pool.length);
          const card = pool.splice(cardIndex, 1)[0];

          // C. Determine Variant (common, silver, golden, foil)
          let variant;

          if (selectedRarity === LEGENDARY_TYPE) {
            // Les légendaires ne sont jamais tirées en silver / golden / foil.
            variant = LEGENDARY_FORCED_VARIANT;
            legendaryCountInBooster++;
          } else {
            const foilRate = variantRates.foil || 0;
            const goldenRate = variantRates.golden || 0;
            const silverRate = variantRates.silver || 0;

            const rngVariant = Math.random() * 100;

            if (rngVariant < foilRate) {
              variant = 'foil';
            } else if (rngVariant < (foilRate + goldenRate)) {
              variant = 'golden';
            } else if (rngVariant < (foilRate + goldenRate + silverRate)) {
              variant = 'silver';
            } else {
              variant = 'common';
            }
          }

          allGeneratedCards.push({
            ...card,
            originalType: card.type,
            frontImage: card.image,
            type: variant
          });
        }
      }

      // 4. Update Profile (Inventory & Card Collection)
      try {
        // Remove boosters
        this.userStore.profile.boosters[this.selectedBooster.id] = Math.max(0, availableCount - actualCount);

        // Add cards
        if (!this.userStore.profile.cards) this.userStore.profile.cards = {};

        allGeneratedCards.forEach(card => {
          if (!this.userStore.profile.cards[card.id]) {
            this.userStore.profile.cards[card.id] = { common: 0, silver: 0, golden: 0, foil: 0 };
          }
          if ((this.userStore.profile.cards[card.id][card.type] || 0) === 0) {
            card.isNew = true;
          }
        });

        allGeneratedCards.forEach(card => {
          this.userStore.profile.cards[card.id][card.type]++;
        });

        if (!this.userStore.profile.stats) {
          this.userStore.profile.stats = { public: true, open: 0, openByCollection: {}, destroy: 0, upgrades: 0, downgrades: 0 };
        }
        if (!this.userStore.profile.stats.openByCollection) {
          this.userStore.profile.stats.openByCollection = {};
        }
        this.userStore.profile.stats.open += actualCount;
        [...effectiveCollectionIds].forEach((collId) => {
          this.userStore.profile.stats.openByCollection[collId] = (this.userStore.profile.stats.openByCollection[collId] || 0) + actualCount;
        });

        // Check for 'chanceux' achievement (Find a Foil)
        if (!this.userStore.profile.achievements) this.userStore.profile.achievements = {};

        if (allGeneratedCards.some(c => c.type === 'foil')) {
          if (!this.userStore.profile.achievements['chanceux']) {
            this.userStore.profile.achievements['chanceux'] = true;
            notifManager.sendAchievementNotif(this.userStore.profile.id, 'chanceux', 'Vous avez obtenues le succès "Chanceux" !');
          }
        }

        if (actualCount >= 10 && !this.userStore.profile.achievements['ouverture_facile']) {
          this.userStore.profile.achievements['ouverture_facile'] = true;
          notifManager.sendAchievementNotif(this.userStore.profile.id, 'ouverture_facile', 'Vous avez obtenu le succès "Ouverture facile" !');
        }

        logsManager.log(this.userStore.profile.name, 'OPEN', `Ouverture de ${actualCount} booster(s) ${this.selectedBooster.name}. Cartes obtenues: ${allGeneratedCards.map(c => c.name + ' (' + c.type + (c.originalType === LEGENDARY_TYPE ? ' - légendaire' : '') + ')').join(', ')}.`);

        await this.userStore.profile.save();

        this.loadInventory();
        achievementsManager.checkForAchievements();

      } catch (e) {
        console.error("Error saving profile", e);
        Swal.fire('Erreur', 'Une erreur est survenue lors de la sauvegarde.', 'error');
      }

      // 5. Prepare View Data
      if (isFull) {
        /*
          L'ORDRE DU CLASSEUR, et non celui de la chance : toutes les cartes
          étant classiques, trier par variante ne dirait rien. On garde l'ordre
          rendu par `fullBoosterCards` — par collection puis par numéro — et on
          se contente de repousser les légendaires à la fin, comme ailleurs.
          Le tri de JavaScript est stable : l'ordre du classeur survit.
        */
        allGeneratedCards.sort((a, b) => {
          const legendary = (card) => (card.originalType === LEGENDARY_TYPE ? 1 : 0);
          return legendary(a) - legendary(b);
        });
      } else {
        allGeneratedCards.sort((a, b) => {
          const rarityOrder = { 'foil': 3, 'golden': 2, 'silver': 1, 'common': 0 };
          // Les légendaires (toujours classiques) sont révélées en dernier.
          const score = (card) => card.originalType === LEGENDARY_TYPE
            ? 100
            : (rarityOrder[card.type] || 0);
          const scoreA = score(a);
          const scoreB = score(b);
          if (scoreA === scoreB) {
            return a.name.localeCompare(b.name);
          }
          return scoreA - scoreB;
        });
      }
      this.cards = allGeneratedCards;

      // START PRELOADING HERE
      const imagePromises = allGeneratedCards.map(card => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = Card.buildImageUrl(card.frontImage);
        });
      });

      // --- Animation Phase ---
      if (this.status !== 'opening') return;

      // Step 2: Shake animation (0.8s)
      this.timeouts.push(setTimeout(() => {
        this.status = 'exploded';

        // Step 3: Explode/Tear animation (0.8s) -> Then Reveal
        const explosionPromise = new Promise(resolve => setTimeout(resolve, 800));

        /*
          ON N'ATTEND PAS INDÉFINIMENT LES IMAGES.

          Cinq cartes se préchargent en un clin d'œil ; une collection entière
          — ce qu'un booster Full distribue — peut en demander cent, et la
          révélation restait alors suspendue sur l'explosion, sans que rien
          n'indique qu'il se passe quelque chose. Au-delà de trois secondes on
          révèle quand même : les images manquantes finissent d'arriver sous
          les yeux du joueur, ce qui est très préférable à un écran figé.
        */
        const preloaded = Promise.race([
          Promise.all(imagePromises),
          new Promise(resolve => setTimeout(resolve, 3000)),
        ]);

        Promise.all([explosionPromise, preloaded]).then(() => {
          if (this.status === 'exploded') {
            this.status = 'revealed';
            this.timeouts = [];
          }
        });
      }, 800));
    },
    skipAnimation() {
      if (this.status === 'opening' || this.status === 'exploded') {
        this.timeouts.forEach(t => clearTimeout(t));
        this.timeouts = [];
        this.status = 'revealed';
      }
    },
    resetBooster(cancel = false) {
      this.timeouts.forEach(t => clearTimeout(t));
      this.timeouts = [];
      this.status = 'idle';
      this.selectedBooster = null;
    },
    reopenBooster() {
      this.timeouts.forEach(t => clearTimeout(t));
      this.timeouts = [];
      this.status = 'idle';
      this.cards = [];
      this.openBooster();
    },
    async deleteBoosterNotifs() {
      if (!this.userStore.profile || !this.userStore.profile.id) return;
      try {
        const notifs = await Notif.getByUser(this.userStore.profile.id);
        const boosterNotifs = notifs.filter(n => n.type === 'BOOSTER');
        await Promise.all(boosterNotifs.map(n => n.delete()));
      } catch (e) {
        console.error("Error deleting booster notifs:", e);
      }
    }
  },
  beforeUnmount() {
    this.unsub.forEach(unsub => {
      if (typeof unsub === 'function') unsub();
    })
  },
}
</script>
